<script lang="ts">
  import { getContext } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Loading from '../etc/loader.svelte';
  import { format } from 'date-fns';
  import { ja } from 'date-fns/locale';
  import { loginUser } from '../components/auth/LoginUser';
  import RepositoryFactory, {
    CONTRACT,
    HATENOHAMA,
    BUNDLE_OPTION_HIKARI01_INHERITED,
    SUB_CONTRACT_HIKARI01_TEL
  } from '../repositories/RepositoryFactory';
  import type {
    GetContractResult,
    GetSubContractListResult,
    GetDataTrafficRemainResult,
    GetMonthlyDataTrafficRouterResult
  } from '../repositories/contract';
  import type { GetDataTrafficResult } from '../repositories/hatneohama';
  import type { BundleOptionHikari01InheritedResult } from '../repositories/bundleoptionhikari01inherited';
  import type { SubContractHikari01TelResult } from '../repositories/subcontracthikari01tel';

  import BundleOptionHikariCollabo from './bundleoption/BundleOptionHikariCollabo.svelte';
  import BundleOptionUmobiled from './bundleoption/BundleOptionUmobiled.svelte';
  import BundleOption from './bundleoption/BundleOption.svelte';
  import BundleOptionHikari01 from './bundleoption/BundleOptionHikari01.svelte';
  import subContractModal from '../etc/modal/ContractOptionChangeModal.svelte';
  import contractWithdrawalModal from '../etc/modal/WithdrawalModal.svelte';
  import DataChargeModal from '../etc/modal/DataChargeModal.svelte';
  import DataChargeHistoryModal from '../etc/modal/DataChargeHistoryModal.svelte';
  import RelocationProcessModal from '../etc/modal/hikari01/contract/RelocationProcessModal.svelte';
  import ApplyIenodenwaModal from '../etc/modal/hikari01/subcontract/ApplyIenodenwaModal.svelte';
  import UnderContractForIenodenwaModal from '../etc/modal/hikari01/subcontract/UnderContractForIenodenwaModal.svelte';
  import UnderContractForGateCallModal from '../etc/modal/hikari01/subcontract/UnderContractForGateCallModal.svelte';
  import OtherOptionModal from '../etc/modal/hikari01/otheroption/OtherOptionModal.svelte';
  import BundleOptionPremium from './bundleoption/BundleOptionPremium.svelte';

  const { open }: any = getContext('simple-modal');
  let contract: GetContractResult;
  let subContractList: GetSubContractListResult[];
  let dataTraffic: GetDataTrafficResult;
  let monthlyDataTrafficRouter: GetMonthlyDataTrafficRouterResult;
  let contractStartDate: string;
  let dataTrafficRemain: GetDataTrafficRemainResult;
  let bundleOptionHikari01InheritedList: BundleOptionHikari01InheritedResult[];
  let subContractHikari01Tel: SubContractHikari01TelResult;

  let promise = getApi();
  let dataTrafficRemainMessage: string = null;
  let monthlyDataTrafficRouterMessage: string = null;

  // 契約情報取得
  async function getApi() {
    const contractRepository = RepositoryFactory[CONTRACT];

    await Promise.all([contractRepository.getContract(), contractRepository.getSubContractList()])
      .then((res) => {
        contract = res[0];
        subContractList = res[1];
      })
      .catch((error) => {
        if (error.response.status === 403) {
          alert('セッションが切れました。再度ログインしてください。');
          loginUser.logout();
          push('/login');
        } else {
          alert('エラーが発生しました。');
        }
        return false;
      });

    const hatenohamaRepository = RepositoryFactory[HATENOHAMA];

    if (isUmobiled()) {
      if (isTerminated(contract)) {
        await Promise.all([hatenohamaRepository.getDataTraffic()])
          .then((res) => {
            if (!res) {
              throw new Error();
            }
            dataTraffic = res[0];
            dataTrafficRemain = null;
            dataTrafficRemainMessage = '解約済のため非表示となります。';
          })
          .catch((error) => errorProcess(error));
      } else {
        await Promise.all([hatenohamaRepository.getDataTraffic(), contractRepository.getDataTrafficRemain()])
          .then((res) => {
            if (!res) {
              throw new Error();
            }
            dataTraffic = res[0];
            dataTrafficRemain = res[1];
            if (dataTrafficRemain.requestErrorFlg) {
              dataTrafficRemainMessage = '通信量が取得できませんでした。';
            } else if (dataTrafficRemain.dataTraficEmptyFlg) {
              dataTrafficRemainMessage = '残量が無いためデータチャージを推奨します。';
            }
          })
          .catch((error) => errorProcess(error));
      }
    } else if (isMobileRouter()) {
      if (isTerminated(contract)) {
        monthlyDataTrafficRouter = null;
        monthlyDataTrafficRouterMessage = '解約済のため非表示となります。';
      } else {
        await contractRepository
          .getMonthlyDataTrafficRouter()
          .then((res) => {
            monthlyDataTrafficRouter = res;

            if (monthlyDataTrafficRouter.requestErrorFlg) {
              monthlyDataTrafficRouterMessage = '通信量が取得できませんでした。';
            }
          })
          .catch((error) => errorProcess(error));
      }
    } else if (isPremium()) {
      await hatenohamaRepository
        .getDataTraffic()
        .then((res) => {
          dataTraffic = res;
        })
        .catch((error) => errorProcess(error));
    } else if (isHikari01) {
      const SubContractHikari01TelRepository = RepositoryFactory[SUB_CONTRACT_HIKARI01_TEL];
      const BundleOptionHikari01InheritedRepository = RepositoryFactory[BUNDLE_OPTION_HIKARI01_INHERITED];

      Promise.all([SubContractHikari01TelRepository.fetchValid(), BundleOptionHikari01InheritedRepository.fetchList()])
        .then((res) => {
          subContractHikari01Tel = res[0];
          bundleOptionHikari01InheritedList = res[1];
        })
        .catch((error) => errorProcess(error));
    }

    contractStartDate = format(new Date(contract.contractStartDate), 'yyyy/MM/dd', { locale: ja });
    return { contract, subContractList, dataTraffic, dataTrafficRemain };
  }

  const errorProcess = (error: any) => {
    if (error.response.status === 403) {
      alert('セッションが切れました。再度ログインしてください。');
      loginUser.logout();
      push('/login');
    } else {
      alert('エラーが発生しました。');
    }
  };

  const isUmobiled = () => {
    return $loginUser?.businessCode === 'UMOBILE_D' || $loginUser?.businessCode === 'UMOBILE_D_ENECOM';
  };

  const isMobileRouter = () => {
    return $loginUser?.businessCode === 'UMOBILE_EA' || $loginUser?.businessCode === 'UMOBILE_SONET';
  };

  const isHikari01 = () => {
    return $loginUser?.businessCode === 'HIKARI_01';
  };

  const isHikaricollabo = () => {
    return $loginUser?.businessCode === 'HIKARI_COLLABO';
  };

  const isWithunet = () => {
    return $loginUser?.businessCode === 'WITHUNET';
  };

  const isPremium = () => {
    return $loginUser?.businessCode === 'UMOBILE_PREMIUM';
  };

  $: isAvailableApplyHikari01Ienodenwa = () => {
    if (!isHikari01()) {
      return false;
    }
    return !subContractHikari01Tel;
  };

  $: isAvailableTerminateHikari01Ienodenwa = () => {
    if (!isHikari01()) {
      return false;
    }
    return subContractHikari01Tel?.telServiceCode === 'HIKARI_01_TEL_IENODENWA';
  };

  $: isAvailableTerminateHikari01GateCall = () => {
    if (!isHikari01()) {
      return false;
    }
    return ['HIKARI_01_TEL_GATE_F', 'HIKARI_01_TEL_GATE_M'].includes(subContractHikari01Tel?.telServiceCode);
  };

  $: isAvailableHikari01OtherOption = () => {
    if (!isHikari01()) {
      return false;
    }
    return bundleOptionHikari01InheritedList && bundleOptionHikari01InheritedList.length > 0;
  };

  $: isHikari01DLN = () => {
    return isHikari01() && contract.serviceCode === 'HIKARI_01_LINE_DLN_VDSL_1';
  };

  $: isAvailableTerminate = () => {
    if (isHikari01DLN()) {
      return false;
    }
    return contract.contractStatusCode !== 'TERMINATED';
  };

  const isTerminated = (contract: GetContractResult): boolean => {
    return contract.contractStatusCode === 'TERMINATED';
  };

  const showSubContractModal = (id: number) => {
    open(subContractModal, { subContractId: id });
  };

  const handleWithdrawalScreenTransition = () => {
    if ($loginUser.businessCode === 'HIKARI_01') {
      push(`/withdrawal/hikari01/`);
    } else {
      showContractWithdrawalModal();
    }
  };

  const showContractWithdrawalModal = () => {
    open(contractWithdrawalModal);
  };

  const showDataChargeModal = () => {
    open(DataChargeModal);
  };

  const showDataChargeHistoryModal = () => {
    open(DataChargeHistoryModal);
  };

  const showRelocationProcessModal = () => {
    open(RelocationProcessModal, {});
  };

  const showApplyIenodenwaModal = () => {
    open(ApplyIenodenwaModal, {});
  };

  const showUnderContractForIenodenwaModal = (subContractId: number) => {
    open(UnderContractForIenodenwaModal, {
      subContractId: subContractId
    });
  };

  const showUnderContractForGateCallModal = (subContractId: number) => {
    open(UnderContractForGateCallModal, {
      subContractId: subContractId
    });
  };

  const showOtherOptionModal = () => {
    open(OtherOptionModal, {});
  };
</script>

{#await promise}
  <Loading />
{:then { contract, subContractList, dataTraffic, dataTrafficRemain }}
  <div class="mb-5 screen-title">
    <span class="text-gray-700 text-2xl font-bold">契約内容</span>
  </div>
  <div class="flex mb-4 bg-gray-50 min-w-full">
    <div class="rounded border-t-4 border-green-400">
      <h3 class="text-xl font-bold m-3">プラン情報</h3>
      <div class="shadow rounded-lg border-1 p-4 mb-10">
        <div class="flex flex-col divide-y divide-gray-300">
          <div class="flex-1 flex flex-col">
            <div class="inline-grid md:grid-cols-3 justify-items-center items-center min-w-full p-4">
              <div class="grid-1 col-span-2 justify-self-start w-full">
                <h4 class="text-lg font-semibold">{contract.serviceName}</h4>
              </div>
              {#if !isWithunet() && !isMobileRouter() && !isHikari01()}
                <div class="grid-1">
                  <button
                    on:click={() => push(`/contract/change/`)}
                    class="button-primary"
                    disabled={isTerminated(contract)}
                  >
                    変更
                  </button>
                </div>
              {/if}
            </div>
            <div class="flex-1">
              <div class="inline-grid grid-cols-3 justify-items-center items-center min-w-full p-4">
                <div class="grid-1 col-span-2 justify-self-start w-full">
                  <div class="grid md:grid-cols-2">
                    <span class="grid-1 label-title">ご利用開始日</span>
                    <span class="grid-1 label-value">{contractStartDate}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="flex-1 flex flex-col">
            <div class="flex-1 pt-4 pl-4 mb-4">
              <h4 class="text-lg font-semibold">
                {contract.contractStatus}
                {#if contract.contractEndDate}
                  （解約日：{contract.contractEndDate}）
                {/if}
              </h4>
            </div>
            <div class="flex-1">
              <div class="inline-grid grid-cols-3 justify-items-center items-center min-w-full px-4">
                <div class="grid-1 col-span-2 justify-self-start w-full">
                  <div class="grid md:grid-cols-2 mb-2">
                    <span class="grid-1 label-title">契約ID</span>
                    <span class="grid-1 label-value">{contract.contractId}</span>
                  </div>
                </div>
              </div>
              <div class="inline-grid grid-cols-3 justify-items-center items-center min-w-full px-4">
                <div class="grid-1 col-span-2 justify-self-start w-full">
                  <div class="grid md:grid-cols-2 mb-2">
                    <span class="grid-1 label-title">契約キー</span>
                    <span class="grid-1 label-value">
                      {#if isUmobiled()}
                        {contract.keyMsisdn} / {contract.keySerial}
                      {:else if isMobileRouter()}
                        {contract.keyMsisdn} / {contract.keySerial} / {contract.keyProductSerial}
                      {:else if isHikari01()}
                        {contract.keyArteriaCustomerId}
                      {:else}
                        {#if contract.keyCaf}
                          {contract.keyCaf}<br />
                        {/if}
                        {#if contract.keyAccessKey}
                          {contract.keyAccessKey}<br />
                        {/if}
                        {#if contract.keyAccount}
                          {contract.keyAccount}<br />
                        {/if}
                      {/if}
                    </span>
                  </div>
                </div>
              </div>
              <!-- 回線設置住所 (光コラボ, withUnet 光01)-->
              {#if isHikaricollabo() || isWithunet() || isHikari01()}
                <div class="inline-grid md:grid-cols-3 justify-items-center items-center min-w-full px-4">
                  <div class="grid-1 col-span-2 w-full">
                    <div class="grid md:grid-cols-2 mb-2">
                      <span class="grid-1 label-title">回線設置住所</span>
                      <span class="grid-1 justify-self-start w-full label-value">
                        {#if contract.locationZipcode}
                          〒{contract.locationZipcode}
                          {contract.locationPrefecture ?? ''}{contract.locationCity ?? ''}{contract.locationStreet ??
                            ''}{contract.locationBuilding ?? ''}
                        {:else}
                          -
                        {/if}
                      </span>
                    </div>
                  </div>
                  {#if isHikari01() && !isHikari01DLN()}
                    <div class="grid-1">
                      <button
                        on:click={showRelocationProcessModal}
                        class="button-primary"
                        disabled={isTerminated(contract)}
                      >
                        変更
                      </button>
                    </div>
                  {/if}
                </div>
              {/if}
              <!-- 利用者 (光コラボ, withUnet, 光01)-->
              {#if isHikaricollabo() || isWithunet() || isHikari01()}
                <div class="inline-grid grid-cols-3 justify-items-center items-center min-w-full px-4">
                  <div class="grid-1 col-span-2 justify-self-start w-full">
                    <div class="grid md:grid-cols-2">
                      <span class="grid-1 label-title">利用者</span>
                      <span class="grid-1 label-value">
                        {#if contract.contractSei || contract.contractMei}
                          {contract.contractSei ?? ''}{contract.contractMei ?? ''}
                          {#if contract.contractSeiKana || contract.contractMeiKana}
                            （{contract.contractSeiKana ?? ''}{contract.contractMeiKana ?? ''})
                          {/if}
                        {:else}
                          -
                        {/if}
                      </span>
                    </div>
                  </div>
                </div>
              {/if}
            </div>
          </div>

          {#if isUmobiled() || isPremium()}
            <div class="flex-1 flex flex-col mt-4">
              <div class="inline-grid md:grid-cols-3 justify-items-center items-center min-w-full p-4">
                <div class="grid-1 justify-self-start">
                  <h4 class="text-lg font-semibold">通信量</h4>
                </div>
                {#if dataTrafficRemainMessage}
                  <div
                    class="grid-1 justify-self-start col-span-2 bg-red-100 border-red-500 text-red-700 px-4 py-2"
                    role="alert"
                  >
                    <span class="font-semibold">
                      {dataTrafficRemainMessage}
                    </span>
                  </div>
                {/if}
              </div>
              {#if isUmobiled()}
                <div class="inline-grid grid-cols-3 justify-items-center items-center min-w-full p-4">
                  <div class="grid-1 justify-self-start">
                    <span class="grid-1 label-title">残量</span>
                  </div>
                  <div class="grid-1 justify-self-start">
                    <span class="grid-1 label-value">
                      {dataTrafficRemain?.dataTrafficRemainMegaByteForDisplay ?? '-'}MB</span
                    >
                  </div>
                  <div class="grid-1">
                    <button
                      on:click={() => showDataChargeModal()}
                      class="button-primary"
                      disabled={isTerminated(contract)}
                    >
                      チャージ
                    </button>
                    <button
                      on:click={showDataChargeHistoryModal}
                      class="button-charge-history"
                      disabled={isTerminated(contract)}
                    >
                      チャージ履歴
                    </button>
                  </div>
                </div>
              {/if}
              <div class="flex-1">
                <div class="inline-grid md:grid-cols-3 justify-items-center items-center min-w-full p-4">
                  <div class="grid-1 col-span-2 justify-self-start w-full">
                    <div class="grid grid-cols-2">
                      <span class="grid-1 label-title">月別通信量</span>
                      <div class="grid-1">
                        <div class="grid grid-cols-2">
                          <span class="grid-1 label-value">
                            {dataTraffic.packetMonthly0.totalMegaByte}MB
                          </span>
                          <span class="grid-1 label-value">(今月)</span>
                        </div>
                        <div class="grid grid-cols-2">
                          <span class="grid-1 label-value">
                            {dataTraffic.packetMonthly1.totalMegaByte}MB
                          </span>
                          <span class="grid-1 label-value">(前月)</span>
                        </div>
                        <div class="grid grid-cols-2">
                          <span class="grid-1 label-value">
                            {dataTraffic.packetMonthly2.totalMegaByte}MB
                          </span>
                          <span class="grid-1 label-value">(前々月)</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="inline-grid md:grid-cols-3 justify-items-center items-center min-w-full p-4">
                  <div class="grid-1 col-span-2 justify-self-start w-full">
                    <div class="grid grid-cols-2">
                      <span class="grid-1 label-title">日別通信量</span>
                      <div class="grid-1">
                        <div class="grid grid-cols-2">
                          <span class="grid-1 label-value">
                            {dataTraffic.packetDaily1.totalMegaByte}MB
                          </span>
                          <span class="grid-1 label-value">(1日前)</span>
                        </div>
                        <div class="grid grid-cols-2">
                          <span class="grid-1 label-value">
                            {dataTraffic.packetDaily2.totalMegaByte}MB
                          </span>
                          <span class="grid-1 label-value">(2日前)</span>
                        </div>
                        <div class="grid grid-cols-2">
                          <span class="grid-1 label-value">
                            {dataTraffic.packetDaily3.totalMegaByte}MB
                          </span>
                          <span class="grid-1 label-value">(3日前)</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="data-traffic-note inline-grid justify-items-center items-center min-w-full p-4">
                  <span class="underline">※通信量に関する注記はこちら</span>
                  <div class="detail">
                    ・1GBは1024MBとなります<br />
                    ・月別通信量の今月分の通信量には、本日分の通信量を含みません
                  </div>
                </div>
              </div>
            </div>
          {/if}

          {#if isMobileRouter()}
            <div class="flex-1 flex flex-col">
              <div class="inline-grid md:grid-cols-3 justify-items-center items-center min-w-full p-4">
                <div class="grid-1 justify-self-start">
                  <h4 class="text-lg font-semibold">通信量</h4>
                </div>
                {#if monthlyDataTrafficRouterMessage}
                  <div
                    class="grid-1 justify-self-start col-span-2 bg-red-100 border-red-500 text-red-700 px-4 py-2"
                    role="alert"
                  >
                    <span class="font-semibold">{monthlyDataTrafficRouterMessage}</span>
                  </div>
                {/if}
              </div>
              <div class="flex-1">
                <div class="inline-grid md:grid-cols-3 justify-items-center items-center min-w-full p-4">
                  <div class="grid-1 col-span-2 justify-self-start w-full">
                    <div class="grid grid-cols-2">
                      <span class="grid-1 label-title">月別通信量</span>
                      <div class="grid-1">
                        <div class="grid grid-cols-2">
                          <span class="grid-1 label-value">
                            {monthlyDataTrafficRouter?.dataTrafficMegaByteCurrentMonth ?? '-'}MB
                          </span>
                          <span class="grid-1 label-value">(今月)</span>
                        </div>
                        <div class="grid grid-cols-2">
                          <span class="grid-1 label-value">
                            {monthlyDataTrafficRouter?.dataTrafficMegaByteLastMonth ?? '-'}MB
                          </span>
                          <span class="grid-1 label-value">(前月)</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="data-traffic-note inline-grid justify-items-center items-center min-w-full p-4">
                  <span class="underline">※通信量に関する注記はこちら</span>
                  <div class="detail">
                    ・1GBは1024MBとなります<br />
                    ・今月分の通信量には本日分の通信量を含みません
                  </div>
                </div>
              </div>
            </div>
          {/if}
        </div>
      </div>

      <!-- 付帯契約があるのみ表示-->
      {#if subContractList?.length > 0}
        <h3 class="text-xl font-bold m-3">付帯契約情報</h3>
        <div class="shadow rounded-lg border-1 mb-10">
          <div class="flex flex-col divide-y divide-gray-300 p-4">
            {#each subContractList as subContract}
              <div class="inline-grid grid-cols-3 justify-items-center items-center min-w-full p-4">
                <div class="grid-1 col-span-2 justify-self-start">
                  <span class="inline-block font-semibold">
                    {subContract.serviceName}
                  </span>
                </div>

                <div class="grid-1">
                  <button on:click={() => showSubContractModal(subContract.subContractId)} class="button-primary">
                    詳細
                  </button>
                </div>
              </div>
            {/each}
          </div>
        </div>
      {/if}

      <!-- 付帯契約(電話情報) ※光01 -->
      {#if isHikari01()}
        <h3 class="text-xl font-bold m-3">付帯契約(電話)情報</h3>
        <!-- 電話契約なしの場合 -->
        {#if isAvailableApplyHikari01Ienodenwa()}
          <div class="shadow rounded-lg border-1 mb-10">
            <div class="flex flex-col divide-y divide-gray-300 p-4">
              <div class="inline-grid grid-cols-3 justify-items-center items-center min-w-full p-4">
                <div class="grid-1 col-span-2 justify-self-start">
                  <span class="inline-block">電話契約はありません</span>
                </div>

                <div class="grid-1">
                  <button on:click={showApplyIenodenwaModal} class="button-primary" disabled={isTerminated(contract)}>
                    詳細
                  </button>
                </div>
              </div>
            </div>
          </div>
        {:else if isAvailableTerminateHikari01Ienodenwa()}
          <!-- いえの電話 解約 -->
          <div class="shadow rounded-lg border-1 mb-10">
            <div class="flex flex-col divide-y divide-gray-300 p-4">
              <div class="inline-grid grid-cols-3 justify-items-center items-center min-w-full p-4">
                <div class="grid-1 col-span-2 justify-self-start">
                  <span class="inline-block font-semibold">{'U-NEXT光01_いえのでんわ'}</span>
                </div>

                <div class="grid-1">
                  <button
                    on:click={() =>
                      showUnderContractForIenodenwaModal(subContractHikari01Tel?.subContractHikari01TelId)}
                    class="button-primary"
                  >
                    詳細
                  </button>
                </div>
              </div>
            </div>
          </div>
        {:else if isAvailableTerminateHikari01GateCall()}
          <!-- GateCallF/M 解約 -->
          <div class="shadow rounded-lg border-1 mb-10">
            <div class="flex flex-col divide-y divide-gray-300 p-4">
              <div class="inline-grid grid-cols-3 justify-items-center items-center min-w-full p-4">
                <div class="grid-1 col-span-2 justify-self-start">
                  <span class="inline-block font-semibold">{'U-NEXT光01_GATECALL050(F)'}</span>
                </div>

                <div class="grid-1">
                  <button
                    on:click={() => showUnderContractForGateCallModal(subContractHikari01Tel?.subContractHikari01TelId)}
                    class="button-primary"
                  >
                    詳細
                  </button>
                </div>
              </div>
            </div>
          </div>
        {/if}
      {/if}

      {#if $loginUser?.businessCode !== 'UMOBILE_SONET'}
        <div class="inline-grid grid-cols-3 justify-items-center items-center min-w-full">
          <div class="grid-1 col-span-2 justify-self-start">
            <h3 class="text-xl font-bold m-3">オプション</h3>
          </div>
          {#if isAvailableHikari01OtherOption()}
            <div class="grid-1 justify-self-center">
              <!-- svelte-ignore a11y-click-events-have-key-events -->
              <!-- svelte-ignore a11y-no-static-element-interactions -->
              <div class="text-base font-bold link" on:click={showOtherOptionModal}>下記以外のオプションはこちら</div>
            </div>
          {:else}
            <div class="grid-1" />
          {/if}
        </div>

        <div class="shadow rounded-lg border-1 mb-10">
          <div class="flex flex-col divide-y divide-gray-300 p-4">
            {#if isHikaricollabo()}
              <BundleOptionHikariCollabo />
            {:else if isUmobiled()}
              <BundleOptionUmobiled />
            {:else if $loginUser?.businessCode === 'UMOBILE_EA' || $loginUser?.businessCode === 'WITHUNET'}
              <BundleOption />
            {:else if isHikari01()}
              <BundleOptionHikari01 />
            {:else if isPremium()}
              <BundleOptionPremium />
            {/if}
          </div>
        </div>
      {/if}

      {#if isAvailableTerminate()}
        <div class="shadow rounded-lg border-1">
          <div class="grid md:grid-cols-3 justify-items-center items-center p-8">
            <div class="grid-1 col-span-2 justify-self-start">
              <span class="inline-block"> 解約をご希望の方はこちらよりお手続きください </span>
            </div>
            <div class="grid-1">
              <button on:click={handleWithdrawalScreenTransition} class="button-withdrawal"> 解約手続きへ </button>
            </div>
          </div>
        </div>
      {/if}
    </div>
  </div>
{:catch error}
  <!-- API取得失敗 -->
{/await}

<style lang="postcss">
  .button-primary {
    @apply bg-green-400 rounded-lg hover:bg-green-500 focus:outline-none text-sm font-semibold text-white px-10 py-2;
  }
  .button-primary:disabled {
    @apply opacity-30 cursor-default bg-gray-400;
  }
  .button-withdrawal {
    @apply bg-red-100 rounded-lg hover:bg-red-200 focus:outline-none text-sm font-semibold text-red-700 px-10 py-2;
  }
  .button-charge-history {
    @apply bg-green-400 rounded-lg hover:bg-green-500 focus:outline-none text-sm font-semibold text-white px-6 py-2;
  }
  .button-charge-history:disabled {
    @apply opacity-30 cursor-default bg-gray-400;
  }
  .label-title {
    @apply text-gray-900 font-semibold;
  }
  .label-value {
    @apply text-gray-900;
  }
  .data-traffic-note {
    @apply relative cursor-pointer block;
  }
  .detail {
    @apply hidden absolute bg-yellow-100;
  }
  .data-traffic-note:hover .detail {
    @apply block top-0 left-1/3;
  }
  .screen-title {
    text-align: left;
  }
  @media screen and (max-width: 640px) {
    .screen-title {
      text-align: center;
    }
  }
  .link {
    @apply underline cursor-pointer;
  }
</style>
